import { DiscoveryAnswer, DiscoveryFormSection, DiscoveryQuestion } from '../../../../types/advise';

export type DiscoverySectionStatus = 'Not Started' | 'In Progress' | 'Complete';

export interface DiscoveryProgress {
  questions: number;
  answered: number;
  required: number;
  requiredAnswered: number;
}

export interface DiscoverySectionProgress extends DiscoveryProgress {
  status: DiscoverySectionStatus;
}

/** What the form shows for a question: its saved answer, or its default until one is saved. */
export const shownAnswer = (question: DiscoveryQuestion): DiscoveryAnswer['answer'] =>
  question.value?.answer !== undefined ? question.value.answer : question.default;

/** The one rule every Discovery count uses; a pick-any question is answered once one option is ticked. */
export const isAnswered = (question: DiscoveryQuestion): boolean => {
  const answer = shownAnswer(question);
  switch (question.field_type) {
    case 'text':
    case 'notes':
    case 'select':
      return typeof answer === 'string' && answer.trim() !== '';
    case 'number':
      return typeof answer === 'number';
    case 'yes_no':
      return typeof answer === 'boolean';
    case 'multi_select':
      return Array.isArray(answer) && answer.length > 0;
  }
};

const countProgress = (questions: DiscoveryQuestion[]): DiscoveryProgress => {
  const required = questions.filter((question) => question.required);
  return {
    questions: questions.length,
    answered: questions.filter(isAnswered).length,
    required: required.length,
    requiredAnswered: required.filter(isAnswered).length
  };
};

export const sectionProgress = (section: DiscoveryFormSection): DiscoverySectionProgress => {
  const progress = countProgress(section.questions);
  let status: DiscoverySectionStatus = 'Not Started';
  // A section with no required question is Complete once every question in it is answered.
  const done =
    progress.required > 0 ? progress.requiredAnswered === progress.required : progress.answered === progress.questions;
  if (progress.questions > 0 && done) status = 'Complete';
  else if (progress.answered > 0) status = 'In Progress';
  return { ...progress, status };
};

export const discoveryProgress = (sections: DiscoveryFormSection[]): DiscoveryProgress =>
  countProgress(sections.flatMap((section) => section.questions));

export const firstIncompleteSection = (sections: DiscoveryFormSection[]): DiscoveryFormSection | undefined =>
  sections.find((section) => section.questions.some((question) => question.required && !isAnswered(question)));
